import { useState } from 'react';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import TasksGraphQL from '@graphql/task.queries';
import { graphQlClient } from '@config/graphqlClient';

interface UpdateTaskStatusParams {
  taskId: string;
  newStatus: string;
  previousStatus?: string;
  reason?: string;
}

interface UseUpdateTaskStatusReturn {
  updateTaskStatus: (params: UpdateTaskStatusParams) => Promise<boolean>;
  loading: boolean;
}

export const useUpdateTaskStatus = (): UseUpdateTaskStatusReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const updateTaskStatus = async (
    params: UpdateTaskStatusParams,
  ): Promise<boolean> => {
    setLoading(true);

    try {
      const input = {
        taskId: params.taskId,
        newStatus: params.newStatus,
        previousStatus: params.previousStatus || undefined,
        reason: params.reason || undefined,
      };

      const data = (await graphQlClient.request(
        TasksGraphQL.mutations.updateTaskStatus,
        {
          input,
        },
      )) as any;

      if (data?.updateTaskStatus?.taskId) {
        dispatch(
          appendActionMessage({
            message: 'Task status updated successfully',
            type: SnackType.SUCCESS,
          }),
        );

        return true;
      }

      return false;
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message:
            error?.response?.errors?.[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { updateTaskStatus, loading };
};
